import { QueryClient } from '@tanstack/react-query'
import { captureException, addBreadcrumb } from './utils/sentry'
import {
  isExpectedNonFatalOperationalError,
  isPhoneOtpRateLimitError,
} from './errors/service-error'

/**
 * TanStack Query configuration
 *
 * Global configuration for server state management:
 * - Default stale time: 5 minutes (override per-hook for hot/cold data)
 * - Default cache time: 10 minutes
 * - Automatic retries with exponential backoff
 * - Mutation errors captured by Sentry in production
 *
 * staleTime tuning guide:
 *   30s  — conversations, messages (Realtime handles live updates)
 *   1min — booking lists (status changes during active rentals)
 *   2min — booking detail, vehicle detail (moderate change frequency)
 *   5min — default (most queries)
 *   10min — user profile (rarely changes), vehicle makes/models
 *   Infinity — auth session (event-driven, never polls)
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,

      // Data stays in cache for 10 minutes
      gcTime: 10 * 60 * 1000,

      // Retry failed requests with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Focus/app-resume refetch made the app feel nervous: tab switches and
      // foreground transitions would kick off background requests across many
      // mounted screens. Product surfaces use explicit pull-to-refresh,
      // realtime invalidation, or mutation invalidation where freshness matters.
      refetchOnWindowFocus: false,

      // Refetch on reconnect
      refetchOnReconnect: true,

      // Don't refetch on mount if data is fresh
      refetchOnMount: true,
    },
    mutations: {
      // Never retry mutations — auth failures are not transient
      retry: 0,

      onError: (error) => {
        if (__DEV__) {
          console.warn('[Tago] Mutation error:', error)
        } else {
          // Capacity only. `isPhoneOtpDeliveryError` used to be the guard here,
          // which meant a provider rejection — our outage — was downgraded to a
          // breadcrumb and never paged anyone. Provider failures now fall
          // through to captureException below.
          if (isPhoneOtpRateLimitError(error)) {
            addBreadcrumb({
              category: 'auth',
              message: 'Phone OTP send refused: at capacity',
              level: 'warning',
            })
            return
          }

          if (isExpectedNonFatalOperationalError(error)) {
            addBreadcrumb({
              category: 'operational',
              message: 'Recoverable provider error',
              level: 'warning',
            })
            return
          }

          // Production: capture all mutation failures in Sentry for visibility
          captureException(error, { source: 'tanstack-mutation' })
        }
      },

      onSuccess: (_data, _variables, _context) => {
        // Breadcrumb for Sentry — helps trace user actions leading to errors
        addBreadcrumb({
          category: 'mutation',
          message: 'Mutation succeeded',
          level: 'info',
        })
      },
    },
  },
})
